import { NetworkStatus } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  CreditCard,
  Download,
  ExternalLink,
  Loader2,
  Receipt,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CREATE_BILLING_PORTAL_SESSION } from "@/graphql/mutations";
import { GET_BILLING_INVOICES_CONNECTION } from "@/graphql/queries";
import { useToast } from "@/hooks/use-toast";
import type { BillingCapabilities, BillingInvoice, PageInfo } from "../types";
import { billingPortalReturnUrl } from "./billingUrls";

interface BillingInvoicesResponse {
  billingInvoicesConnection?: {
    nodes?: BillingInvoice[] | null;
    pageInfo?: PageInfo | null;
  } | null;
}

interface BillingPortalSessionResponse {
  createBillingPortalSession?: {
    success?: boolean;
    url?: string | null;
    error?: string | null;
  } | null;
}

type InvoiceStatusTone = "success" | "warning" | "danger";
const MISSING_BILLING_ACCOUNT_REASON = "no stripe billing account found for this tenant";

const statusStyles: Record<InvoiceStatusTone, { icon: typeof CheckCircle; className: string }> = {
  success: {
    icon: CheckCircle,
    className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  },
  warning: {
    icon: Clock,
    className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  },
  danger: {
    icon: AlertCircle,
    className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  },
};

function normalizeInvoiceStatus(status: string) {
  return status.trim().toLowerCase();
}

function invoiceStatusLabel(status: string) {
  switch (normalizeInvoiceStatus(status)) {
    case "paid":
      return "Paid";
    case "open":
      return "Open";
    case "draft":
      return "Draft";
    case "void":
      return "Voided";
    case "uncollectible":
      return "Failed";
    default:
      return status || "Unknown";
  }
}

function invoiceStatusTone(status: string): InvoiceStatusTone {
  switch (normalizeInvoiceStatus(status)) {
    case "paid":
      return "success";
    case "open":
    case "draft":
      return "warning";
    default:
      return "danger";
  }
}

function titleize(value: string) {
  return value
    .split("_")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function invoiceDescription(invoice: BillingInvoice) {
  if (!invoice.billingReason) {
    return "Billing invoice";
  }

  switch (invoice.billingReason) {
    case "prepaid_topup":
      return `Prepaid topup for ${formatUsdLabel(invoice.amountPaidUsd || invoice.amountDueUsd)}`;
    case "subscription_create":
      return "Subscription start";
    case "subscription_cycle":
      return "Subscription renewal";
    case "final_overage":
      return "Final overage invoice";
    case "manual":
      return "Usage or final invoice";
    default:
      return titleize(invoice.billingReason);
  }
}

function formatUsdLabel(amount: number) {
  const normalizedAmount = Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2);
  return `${normalizedAmount} USD`;
}

function invoiceAmount(invoice: BillingInvoice) {
  return normalizeInvoiceStatus(invoice.status) === "paid"
    ? invoice.amountPaidUsd
    : invoice.amountDueUsd;
}

function invoiceLink(invoice: BillingInvoice) {
  return invoice.hostedInvoiceUrl || invoice.invoicePdfUrl || null;
}

function normalizeBillingReason(reason?: string | null) {
  return reason?.trim().toLowerCase() || "";
}

function isMissingBillingAccountReason(reason?: string | null) {
  return normalizeBillingReason(reason) === MISSING_BILLING_ACCOUNT_REASON;
}

function billingPortalUnavailableMessage(reason?: string | null) {
  if (isMissingBillingAccountReason(reason)) {
    return "This workspace does not have a billing profile yet. Billing becomes available after the first paid subscription charge or credit top-up.";
  }

  return reason || "Billing portal is unavailable right now.";
}

function invoiceEmptyStateCopy(reason?: string | null) {
  if (isMissingBillingAccountReason(reason)) {
    return {
      title: "No billing history yet",
      description:
        "Invoices and receipts will appear here after this workspace has its first paid subscription charge or credit top-up.",
    };
  }

  return {
    title: "No invoices yet",
    description: "Subscription charges, overage invoices, and prepaid top-ups will appear here.",
  };
}

interface BillingHistoryTabProps {
  billingCapabilities: BillingCapabilities | null;
  showPortalButton?: boolean;
}

const BILLING_INVOICES_PAGE_SIZE = 20;
const EMPTY_PAGE_INFO: PageInfo = {
  startCursor: null,
  endCursor: null,
  hasNextPage: false,
  hasPreviousPage: false,
};

export function BillingHistoryTab({ billingCapabilities, showPortalButton = true }: BillingHistoryTabProps) {
  const { toast } = useToast();
  const { data, loading, error, fetchMore, networkStatus } = useQuery<BillingInvoicesResponse>(GET_BILLING_INVOICES_CONNECTION, {
    variables: { first: BILLING_INVOICES_PAGE_SIZE },
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });
  const [createBillingPortalSession, { loading: openingPortal }] = useMutation<BillingPortalSessionResponse>(
    CREATE_BILLING_PORTAL_SESSION,
  );

  const invoices = data?.billingInvoicesConnection?.nodes || [];
  const pageInfo = data?.billingInvoicesConnection?.pageInfo || EMPTY_PAGE_INFO;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const initialLoading = loading && invoices.length === 0;
  const hasInvoices = invoices.length > 0;
  const billingPortalEnabled = Boolean(billingCapabilities?.billingPortalEnabled);
  const billingPortalReason = billingCapabilities?.billingPortalUnavailableReason;
  const portalUnavailableCopy = billingPortalUnavailableMessage(billingPortalReason);
  const emptyStateCopy = invoiceEmptyStateCopy(billingPortalReason);

  const handleLoadMore = async () => {
    if (!pageInfo.hasNextPage || !pageInfo.endCursor || loadingMore) {
      return;
    }

    await fetchMore({
      variables: {
        first: BILLING_INVOICES_PAGE_SIZE,
        after: pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.billingInvoicesConnection) {
          return previousResult;
        }

        const previousNodes = previousResult.billingInvoicesConnection?.nodes || [];
        const nextNodes = fetchMoreResult.billingInvoicesConnection.nodes || [];
        const existingIds = new Set(previousNodes.map((invoice) => invoice.id));

        return {
          billingInvoicesConnection: {
            ...fetchMoreResult.billingInvoicesConnection,
            nodes: [
              ...previousNodes,
              ...nextNodes.filter((invoice) => !existingIds.has(invoice.id)),
            ],
          },
        };
      },
    });
  };

  const handleManageBilling = async () => {
    if (!billingPortalEnabled) {
      toast({
        title: "Billing portal unavailable",
        description: portalUnavailableCopy,
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: response } = await createBillingPortalSession({
        variables: { returnUrl: billingPortalReturnUrl("overview") },
      });

      if (!response?.createBillingPortalSession?.success || !response.createBillingPortalSession.url) {
        throw new Error(
          response?.createBillingPortalSession?.error || "Unable to open billing portal right now.",
        );
      }

      const billingWindow = window.open(response.createBillingPortalSession.url, "_blank", "noopener,noreferrer");
      if (!billingWindow) {
        window.location.href = response.createBillingPortalSession.url;
      }
    } catch (portalError) {
      toast({
        title: "Billing portal unavailable",
        description:
          portalError instanceof Error
            ? portalError.message
            : "Please try again or contact support@flowlabs.live.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {showPortalButton && (
        <Card className="border-border bg-card p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Invoices</h3>
                  <p className="text-sm text-muted-foreground">
                    Review invoices, receipts, and payment status.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleManageBilling}
                disabled={openingPortal || !billingPortalEnabled}
                className="h-9 rounded-full border-border/60 bg-transparent px-4 text-sm font-medium text-muted-foreground shadow-none hover:border-border hover:bg-muted/40 hover:text-foreground"
              >
                {openingPortal ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Manage billing
              </Button>
            </div>

            {!billingPortalEnabled && billingPortalReason && (
              <div className="flex items-start gap-3 border-t border-border/60 pt-4">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">Billing portal unavailable</p>
                  <p className="text-sm text-muted-foreground">{portalUnavailableCopy}</p>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {initialLoading ? (
        <Card className="border-border p-12 text-center text-muted-foreground">
          <div className="inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading invoices...
          </div>
        </Card>
      ) : error ? (
        <Card className="border-border p-12 text-center">
          <div className="space-y-3 text-muted-foreground">
            <AlertCircle className="w-16 h-16 mx-auto text-muted-foreground/30" />
            <div>
              <p className="text-lg font-medium text-foreground">Could not load invoice history</p>
              <p className="text-sm">Please refresh and try again in a moment.</p>
            </div>
          </div>
        </Card>
      ) : !hasInvoices ? (
        <Card className="border-border p-12 text-center">
          <div className="space-y-3 text-muted-foreground">
            <Receipt className="w-16 h-16 mx-auto text-muted-foreground/30" />
            <div>
              <p className="text-lg font-medium text-foreground">{emptyStateCopy.title}</p>
              <p className="text-sm">{emptyStateCopy.description}</p>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden border-border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold">Date</th>
                  <th className="text-left p-4 text-sm font-semibold">Description</th>
                  <th className="text-left p-4 text-sm font-semibold">Payment Method</th>
                  <th className="text-left p-4 text-sm font-semibold">Status</th>
                  <th className="text-right p-4 text-sm font-semibold">Amount</th>
                  <th className="text-center p-4 text-sm font-semibold">Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invoices.map((invoice) => {
                  const tone = invoiceStatusTone(invoice.status);
                  const status = statusStyles[tone];
                  const StatusIcon = status.icon;
                  const formattedDate = new Date(invoice.invoiceCreatedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  });
                  const amount = invoiceAmount(invoice);
                  const link = invoiceLink(invoice);
                  const rawBrand = invoice.paymentMethodBrand || "";
                  const displayBrand = rawBrand
                    ? rawBrand.charAt(0).toUpperCase() + rawBrand.slice(1)
                    : "Card";
                  const paymentMethod = invoice.paymentMethodLast4
                    ? `${displayBrand} •••• ${invoice.paymentMethodLast4}`
                    : "—";

                  return (
                    <tr key={invoice.id} className="hover:bg-muted/30 transition-colors">
                      <td className="p-4 text-base">{formattedDate}</td>
                      <td className="p-4 text-base">{invoiceDescription(invoice)}</td>
                      <td className="p-4 text-base text-muted-foreground">{paymentMethod}</td>
                      <td className="p-4">
                        <Badge className={`${status.className} gap-1`}>
                          <StatusIcon className="w-3 h-3" />
                          {invoiceStatusLabel(invoice.status)}
                        </Badge>
                      </td>
                      <td className="p-4 text-right font-medium text-base">
                        ${amount.toFixed(2)} {invoice.currency.toUpperCase()}
                      </td>
                      <td className="p-4 text-center">
                        {link ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(link, "_blank", "noopener,noreferrer")}
                            className="h-8 px-2"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageInfo.hasNextPage && (
            <div className="border-t border-border px-4 py-4">
              <Button
                variant="outline"
                onClick={() => void handleLoadMore()}
                disabled={loadingMore}
                className="w-full sm:w-auto"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading more
                  </>
                ) : (
                  "Load more invoices"
                )}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
